import React, { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowRight, Lightbulb, Target } from 'lucide-react';
import './AboutSection.css';
import './AboutSection.motion.css';
import useInViewOnce from './hooks/useInViewOnce';
import useCardTilt from './hooks/useCardTilt';
import usePointerParallax from './hooks/usePointerParallax';
import { POINTER_FX_TABLET_QUERY } from './hooks/useMotionPrefs';
import useFitScale from './hooks/useFitScale';
import { CONTACT } from './profile';
import { trackEvent } from './analytics';

import Portrait from './Portrait';
import blob1 from './images/design/about-blob-1.svg';
import blob2 from './images/design/about-blob-2.svg';
import blob3 from './images/design/about-blob-3.svg';

const VISUAL_W = 400;
const VISUAL_H = 300;

const AboutVisual = () => {
    const { t } = useTranslation();
    const [ref, scale] = useFitScale(VISUAL_W);
    usePointerParallax(ref, { query: POINTER_FX_TABLET_QUERY });
    useCardTilt(ref, { selector: '.quote-card' });

    return (
        <div className="about-visual" ref={ref} style={{ height: VISUAL_H * scale }} data-inview>
            <div className="about-artboard" dir="ltr" style={{ transform: `scale(${scale})` }}>
                <img src={blob1} alt="" className="deco al al--glow-1" style={{ left: -12, top: 18, width: 420, height: 291 }} />
                <img src={blob2} alt="" className="deco al al--glow-2" style={{ left: 174, top: 33, width: 240, height: 251 }} />
                <img src={blob3} alt="" className="deco al al--glow-3" style={{ left: 64, top: 10, width: 160, height: 178 }} />
                <Portrait sizes="260px" className="abs al al--portrait" style={{ left: 72, top: 10, width: 256.7, height: 290 }} />

                <div className="abs al al--quote quote-card" style={{ left: 10, top: 48 }}>
                    <span className="quote-mark" aria-hidden="true">“</span>
                    {t('about.quote_words', { returnObjects: true }).map(word => <span key={word}>{word}</span>)}
                </div>

                <p className="abs al al--signature signature" style={{ left: 22, top: 212 }}>
                    <span className="signature-first">Youssef</span>
                    <span className="signature-last">Ben Henda</span>
                </p>
            </div>
        </div>
    );
};

const AboutSection = () => {
    const { t } = useTranslation();
    const sectionRef = useRef(null);
    const cardsRef = useRef(null);
    useInViewOnce(sectionRef);
    useCardTilt(cardsRef, { selector: '.about-card' });

    const cards = [
        { key: 'drives', Icon: Lightbulb, bg: '#fff6e5', fg: '#f59e0b', href: '#Projects' },
        { key: 'approach', Icon: Target, bg: '#eaf1ff', fg: '#2563eb', href: '#Experience' },
    ];

    return (
        <section className="section about" id="About" ref={sectionRef}>
            <div className="container about-content">
                <div className="about-text" data-inview>
                    <div className="section-header">
                        <span className="eyebrow">{t('about.eyebrow')}</span>
                        <h2 className="section-title">
                            <span className="about-title-line">{t('about.title_l1')}</span>
                            <span className="about-title-line">{t('about.title_l2')}</span>
                        </h2>
                    </div>
                    <p className="about-bio">{t('about.bio')}</p>
                    <a
                        href={CONTACT.linkedin}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn btn-primary btn-sm"
                        onClick={() => trackEvent('Social Click', { network: 'linkedin', location: 'about' })}
                    >
                        {t('about.more')}
                        <ArrowRight size={16} className="flip-rtl" />
                    </a>
                </div>

                <AboutVisual />

                <div className="about-cards" ref={cardsRef} data-inview>
                    {cards.map(({ key, Icon, bg, fg, href }) => (
                        <a key={key} href={href} className={`about-card about-card--${key}`}>
                            <span className="about-card-icon" style={{ background: bg, color: fg }}>
                                <Icon size={24} />
                            </span>
                            <span className="about-card-text">
                                <strong>{t(`about.${key}_title`)}</strong>
                                <span>{t(`about.${key}_text`)}</span>
                            </span>
                            <ArrowRight size={16} className="about-card-arrow flip-rtl" />
                        </a>
                    ))}
                </div>
            </div>
        </section>
    );
};

export default AboutSection;
